import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { systemApi } from '../../api';
import { Badge, Button, Loading } from '../../components/common';
import { useModel } from '../../context/ModelContext';
import { useToast } from '../../context/ToastContext';
import '../pages.css';

/** Worker, GPU, configuration and readiness — the "why is nothing working" page. */
export function SystemTab() {
  const toast = useToast();
  const navigate = useNavigate();
  const { refreshStatus } = useModel();
  const [status, setStatus] = useState(null);
  const [ready, setReady] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusPayload, readyPayload] = await Promise.all([
        systemApi.status(),
        systemApi
          .ready()
          .catch((cause) => (cause.details?.ready === undefined ? null : cause.details)),
      ]);
      setStatus(statusPayload);
      setReady(readyPayload);
      refreshStatus();
    } catch (cause) {
      toast.error(cause, { title: 'Could not read system status' });
    } finally {
      setLoading(false);
    }
  }, [refreshStatus, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !status) return <Loading label="Reading system status…" large />;

  const worker = status?.worker || {};
  const config = status?.config || {};
  const runtime = status?.runtime || {};
  const offline = Boolean(worker.error);
  const vllm = status?.inference_backend === 'vllm';
  const serving = status?.serving || {};
  const servers = serving.servers || [];

  return (
    <div className="col" style={{ gap: 16, maxWidth: 860 }}>
      <div className="row">
        <Button size="sm" variant="ghost" onClick={load}>
          Refresh
        </Button>
        {offline ? (
          <Badge tone="danger">worker offline</Badge>
        ) : (
          <Badge tone="ok">worker online</Badge>
        )}
        {ready?.ready === false && <Badge tone="warn">not ready</Badge>}
      </div>

      {offline && (
        <section className="card card--pad">
          <strong>The inference worker is not reachable</strong>
          <div className="small muted" style={{ marginTop: 6 }}>
            {worker.error}
          </div>
          <pre
            className="mono"
            style={{
              background: 'var(--surface-input)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius)',
              padding: 11,
              marginTop: 10,
              overflowX: 'auto',
            }}
          >{`cd backend
python run_worker.py
# or
docker compose up -d worker`}</pre>
        </section>
      )}

      <section className="card card--pad">
        <div className="row" style={{ marginBottom: 8 }}>
          <div className="section-label">Generation</div>
          <div className="grow" />
          {vllm && (
            <Button size="sm" variant="ghost" onClick={() => navigate('/settings/serving')}>
              Model serving →
            </Button>
          )}
        </div>
        <Row
          label="Backend"
          value={
            vllm
              ? 'vLLM — continuous batching, many users at once'
              : 'HF worker — one request at a time'
          }
        />
        {vllm && (
          <>
            <Row
              label="Model server"
              value={serving.model_server?.reachable ? 'reachable' : serving.model_server?.error}
            />
            <Row
              label="Loaded base models"
              value={
                servers.length
                  ? servers
                      .map((server) => `${server.model_source || server.key} (${server.state})`)
                      .join(', ')
                  : 'none'
              }
            />
            <Row
              label="Policy"
              value={
                serving.policy?.multi_model
                  ? `several models, up to ${serving.policy.max_models}`
                  : 'one model at a time'
              }
            />
            <Row
              label="Admission (this process)"
              value={`${serving.admission?.in_use ?? 0} / ${serving.admission?.capacity ?? '—'} in flight · ${
                serving.admission?.waiting ?? 0
              } waiting`}
            />
          </>
        )}
        <Row
          label="Batch jobs"
          value={
            status?.batch_executor === 'queue'
              ? 'queued in PostgreSQL, run by the runner service'
              : 'run on an API thread'
          }
        />
        <Row
          label="Search indexing"
          value={
            status?.vector_indexing === 'outbox'
              ? 'deferred (outbox, indexed by the runner)'
              : 'inline, during the request'
          }
        />
      </section>

      {ready?.problems?.length > 0 && (
        <section className="card card--pad">
          <strong>Blocking issues</strong>
          <ul className="small muted" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {ready.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="card card--pad">
        <div className="section-label" style={{ marginBottom: 8 }}>
          {vllm ? 'GPU worker (embeddings and serving preparation)' : 'Inference worker'}
        </div>
        <Row label="Engine" value={worker.engine || '—'} />
        <Row label="Device" value={worker.device || '—'} />
        <Row label="GPU" value={worker.gpu_name || '—'} />
        <Row
          label="VRAM"
          value={
            worker.vram_total_gb
              ? `${worker.vram_used_gb?.toFixed(2)} / ${worker.vram_total_gb?.toFixed(2)} GB`
              : '—'
          }
        />
        {!vllm && (
          <>
            <Row label="Active version" value={worker.active_version || 'none loaded'} />
            <Row label="Adapter parameters" value={worker.adapter_params ?? '—'} />
            <Row label="Cached adapters" value={(worker.cached_versions || []).join(', ') || '—'} />
          </>
        )}
        <Row label="Busy" value={worker.busy ? `yes (${worker.busy_for_s}s)` : 'no'} />
        <Row
          label="Requests"
          value={`${worker.requests_served ?? 0} served · ${worker.requests_failed ?? 0} failed`}
        />
        <Row
          label="Uptime"
          value={worker.worker_uptime_s ? `${Math.round(worker.worker_uptime_s / 60)} min` : '—'}
        />
      </section>

      <section className="card card--pad">
        <div className="section-label" style={{ marginBottom: 8 }}>
          Configuration
        </div>
        <Row label="Checkpoints directory" value={config.checkpoints_dir} mono />
        <Row
          label="Experiments directory"
          value={config.experiments_dir || 'not configured'}
          mono
        />
        <Row label="Max upload" value={`${config.max_upload_mb} MB`} />
        <Row label="Max batch images" value={config.max_batch_images} />
        <Row label="Max compare versions" value={config.max_compare_versions} />
        <Row label="API key auth" value={config.api_key_enabled ? 'enabled' : 'disabled'} />
        <div className="xs subtle" style={{ marginTop: 8 }}>
          Upload, batch and comparison limits are the values in force; a superadmin changes them in
          Settings → General.
        </div>
      </section>

      <section className="card card--pad">
        <div className="section-label" style={{ marginBottom: 8 }}>
          Runtime
        </div>
        <Row label="Python" value={runtime.python} />
        <Row label="Platform" value={runtime.platform} mono />
      </section>
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="kv">
      <span className="kv__key">{label}</span>
      <span className={`kv__value ${mono ? 'mono' : ''}`}>{String(value ?? '—')}</span>
    </div>
  );
}
